import { mtFetch, type FetchResult } from "./mtFetch";

export type FeedbackSide = "source" | "target";
export type FeedbackAnchorKind = "constraint" | "offset";
export type FeedbackStatus =
  | "open"
  | "in_progress"
  | "resolved"
  | "wontfix"
  | "orphaned";

export type FeedbackCategory =
  | "incorrect_translation"
  | "abbreviation_issue"
  | "missing_terminology"
  | "morphology_grammar"
  | "style_register"
  | "redundant_addition"
  | "omission"
  | "other";

export interface TranslationFeedback {
  feedback_id: string;
  draft_id: string;
  author: string;
  side: FeedbackSide;
  anchor_kind: FeedbackAnchorKind;
  constraint_id: string | null;
  offset_start: number | null;
  offset_end: number | null;
  selected_text: string;
  context_before: string | null;
  context_after: string | null;
  paired_text: string | null;
  categories: FeedbackCategory[];
  comment_text: string | null;
  status: FeedbackStatus;
  resolution_note: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  linked_action: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListForDraft {
  schema_version: string;
  draft_id: string;
  count: number;
  items: TranslationFeedback[];
}

export interface FeedbackAdminListFilters {
  status?: FeedbackStatus;
  category?: FeedbackCategory;
  author?: string;
  draft_id?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface FeedbackAdminList {
  schema_version: string;
  total: number;
  count: number;
  limit: number;
  offset: number;
  filters: Record<string, string | null>;
  items: TranslationFeedback[];
}

export interface FeedbackDetail {
  schema_version: string;
  item: TranslationFeedback & {
    draft: {
      draft_id: string;
      source_text: string | null;
      target_text: string | null;
      source_lang: string | null;
      target_lang: string | null;
      status: string | null;
    };
  };
}

export interface SubmitFeedbackBody {
  side: FeedbackSide;
  anchor_kind: FeedbackAnchorKind;
  constraint_id?: string | null;
  offset_start?: number | null;
  offset_end?: number | null;
  selected_text: string;
  context_before?: string | null;
  context_after?: string | null;
  paired_text?: string | null;
  categories: FeedbackCategory[];
  comment_text?: string | null;
}

export function submitFeedback(
  draftId: string,
  body: SubmitFeedbackBody,
): Promise<FetchResult<TranslationFeedback>> {
  return mtFetch<TranslationFeedback>(
    `/v1/translation-drafts/${encodeURIComponent(draftId)}/feedback`,
    { method: "POST", body },
  );
}

export function getDraftFeedback(
  draftId: string,
): Promise<FetchResult<FeedbackListForDraft>> {
  return mtFetch<FeedbackListForDraft>(
    `/v1/translation-drafts/${encodeURIComponent(draftId)}/feedback`,
  );
}

export function getAdminFeedbackList(
  filters: FeedbackAdminListFilters = {},
): Promise<FetchResult<FeedbackAdminList>> {
  const query: Record<string, string | number | undefined> = {};
  if (filters.status) query.status = filters.status;
  if (filters.category) query.category = filters.category;
  if (filters.author) query.author = filters.author;
  if (filters.draft_id) query.draft_id = filters.draft_id;
  if (filters.q) query.q = filters.q;
  if (filters.limit !== undefined) query.limit = filters.limit;
  if (filters.offset !== undefined) query.offset = filters.offset;
  return mtFetch<FeedbackAdminList>("/admin/feedback-list", { query });
}

export function getAdminFeedback(
  feedbackId: string,
): Promise<FetchResult<FeedbackDetail>> {
  return mtFetch<FeedbackDetail>(
    `/admin/feedback-item/${encodeURIComponent(feedbackId)}`,
  );
}

export function updateFeedbackStatus(
  feedbackId: string,
  body: { status: FeedbackStatus; resolution_note?: string | null },
): Promise<FetchResult<FeedbackDetail>> {
  return mtFetch<FeedbackDetail>(
    `/admin/feedback-item/${encodeURIComponent(feedbackId)}`,
    { method: "PATCH", body },
  );
}

export interface TerminologyLookupCandidate {
  item_type: "glossary_term" | "abbreviation" | "term_alias";
  item_id: string;
  concept_key: string | null;
  source_text: string | null;
  source_expansion?: string | null;
  target_text: string | null;
  target_abbreviation?: string | null;
  domain: string | null;
  status: string | null;
  score: number;
  in_draft: boolean;
  matched_field?: string;
}

export interface TerminologyLookupResponse {
  schema_version: string;
  query_text: string;
  query_lemma: string | null;
  target_lang: string;
  draft_id: string | null;
  count: number;
  items: TerminologyLookupCandidate[];
}

export function lookupTerminologyByText(body: {
  text: string;
  target_lang: "ru" | "en";
  draft_id?: string;
  limit?: number;
}): Promise<FetchResult<TerminologyLookupResponse>> {
  return mtFetch<TerminologyLookupResponse>("/v1/terminology/lookup-by-text", {
    method: "POST",
    body,
  });
}

export interface AttachActionResponse {
  schema_version: string;
  feedback_id: string;
  status: FeedbackStatus;
  linked_action: Record<string, unknown>;
}

export function attachFeedbackAction(
  feedbackId: string,
  body: {
    action_kind: string;
    action_item_id: string;
    extra?: Record<string, unknown>;
  },
): Promise<FetchResult<AttachActionResponse>> {
  return mtFetch<AttachActionResponse>(
    `/admin/feedback-item/${encodeURIComponent(feedbackId)}/attach-action`,
    { method: "POST", body },
  );
}

export const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  incorrect_translation: "Неверный перевод",
  abbreviation_issue: "Аббревиатура",
  missing_terminology: "Нет в словаре",
  morphology_grammar: "Морфология/грамматика",
  style_register: "Стиль / регистр",
  redundant_addition: "Лишнее добавлено",
  omission: "Пропуск",
  other: "Другое",
};

export const FEEDBACK_STATUS_LABELS: Record<FeedbackStatus, string> = {
  open: "Открыт",
  in_progress: "В работе",
  resolved: "Решён",
  wontfix: "Won't fix",
  orphaned: "Потерян",
};
